import React, { useMemo, useState } from "react";
import {
  useListInvitations,
  useCreateInvitation,
  useResendInvitation,
  useGetMe,
  getListInvitationsQueryKey,
  type UserRole,
  type Invitation,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Plus, Loader2, RotateCw, Link2, Copy } from "lucide-react";

// Only pending email invitations can be re-emailed. Accepted/attended invitees
// have already joined; expired/declined/no-show invites need an explicit
// re-invite, not a silent re-send; non-email channels have no inbox.
function canResend(invitation: Invitation): boolean {
  return invitation.channel === "email" && invitation.status === "invited";
}

const ROLE_LABELS: Record<UserRole, string> = {
  agent: "Agent",
  master_dealer: "Master Dealer",
  account_manager: "Account Manager",
  admin: "Admin",
  finance: "Finance",
  support: "Support",
  poc_support: "PoC Support",
  operations: "Operations",
};

// Which roles each actor may invite. Mirrors the server-side authorization so
// the UI only offers valid choices (the server rejects anything out of bounds).
const INVITABLE_ROLES: Record<string, UserRole[]> = {
  admin: [
    "agent",
    "master_dealer",
    "account_manager",
    "admin",
    "finance",
    "poc_support",
    "operations",
  ],
  account_manager: ["agent", "master_dealer"],
  master_dealer: ["agent"],
};

function invitableFor(role: string | undefined): UserRole[] {
  return (role && INVITABLE_ROLES[role]) || ["agent"];
}

export function InvitationsList() {
  const { data: invitationsPage, isLoading, error } = useListInvitations();
  const invitations = invitationsPage?.items ?? [];
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const resendInvitation = useResendInvitation();
  const [resendingId, setResendingId] = useState<number | null>(null);
  const { data: me } = useGetMe();

  const handleResend = async (invitation: Invitation) => {
    setResendingId(invitation.id);
    try {
      const result = await resendInvitation.mutateAsync({ id: invitation.id });
      queryClient.invalidateQueries({ queryKey: getListInvitationsQueryKey() });
      if (result.emailDelivered) {
        toast({
          title: "Invitation resent",
          description: `Email re-sent to ${invitation.recipientContact}.`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Couldn't resend the email",
          description:
            result.emailError ??
            "The email provider rejected delivery. Please try again.",
        });
      }
    } catch {
      toast({
        variant: "destructive",
        title: "Couldn't resend invitation",
        description: "Please try again.",
      });
    } finally {
      setResendingId(null);
    }
  };

  const stats = useMemo(() => {
    const list = invitations;
    return {
      sent: list.length,
      accepted: list.filter((i) => ["accepted", "attended"].includes(i.status))
        .length,
      pending: list.filter((i) => i.status === "invited").length,
      expired: list.filter((i) =>
        ["expired", "declined", "no_show"].includes(i.status),
      ).length,
    };
  }, [invitations]);

  if (isLoading)
    return (
      <div className="p-6 animate-pulse">
        <div className="h-8 bg-gray-200 w-1/4 rounded mb-6"></div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  if (error)
    return <div className="p-6 text-red-500">Failed to load invitations.</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[#0B132B]">Invitations</h1>
        <Button
          onClick={() => setOpen(true)}
          className="bg-[#E6007E] hover:bg-[#c4006b] text-white"
        >
          <Plus className="w-4 h-4 mr-2" /> Send invitation
        </Button>
      </div>

      {me?.role === "master_dealer" && me?.dealerCode && (
        <ReferralLinkCard dealerCode={me.dealerCode} />
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Invites sent" value={stats.sent} />
        <StatCard label="Accepted" value={stats.accepted} accent="#00A859" />
        <StatCard label="Pending" value={stats.pending} />
        <StatCard label="Expired / declined" value={stats.expired} />
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#0B132B]">
            Recent invitations
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead>Recipient Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Invited as</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invitations.map((invitation) => (
                <TableRow key={invitation.id}>
                  <TableCell className="font-medium">
                    {invitation.recipientFirstName}{" "}
                    {invitation.recipientLastName}
                  </TableCell>
                  <TableCell>{invitation.recipientContact || "-"}</TableCell>
                  <TableCell>
                    {invitation.targetRole
                      ? ROLE_LABELS[invitation.targetRole]
                      : "Agent"}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={invitation.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    {canResend(invitation) ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleResend(invitation)}
                        disabled={resendingId === invitation.id}
                        className="border-gray-200 text-[#0B132B] hover:text-[#E6007E] hover:border-[#E6007E]"
                      >
                        {resendingId === invitation.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <>
                            <RotateCw className="w-3.5 h-3.5 mr-1.5" />
                            Resend
                          </>
                        )}
                      </Button>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {invitations.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-center p-8 text-gray-500"
                  >
                    No invitations yet. Send one to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <SendInviteDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

function ReferralLinkCard({ dealerCode }: { dealerCode: string }) {
  const { toast } = useToast();
  const link = `${window.location.origin}${import.meta.env.BASE_URL}join?ref=${dealerCode}`.replace(/\/\//g, "/").replace(":/", "://");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast({ title: "Link copied", description: "Share it with potential agents." });
    } catch {
      toast({ variant: "destructive", title: "Copy failed", description: "Please copy the link manually." });
    }
  };

  return (
    <Card className="border-[#00A859]/20 bg-[#00A859]/5 shadow-sm">
      <CardContent className="p-4 flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-[#00A859]/15 flex items-center justify-center shrink-0 mt-0.5">
          <Link2 className="w-4 h-4 text-[#00A859]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#0B132B] mb-1">Your referral link</p>
          <p className="text-xs text-gray-500 mb-2">
            Share this link with potential agents. They'll be automatically linked to your dealership when they sign up.
          </p>
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2">
            <span className="text-xs text-gray-600 truncate flex-1 font-mono">{link}</span>
            <button
              onClick={copy}
              className="shrink-0 text-[#0B132B] hover:text-[#E6007E] transition-colors"
              title="Copy link"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatCard({
  label,
  value,
  accent = "#0B132B",
}: {
  label: string;
  value: number;
  accent?: string;
}) {
  return (
    <Card className="border-gray-100 shadow-sm">
      <CardContent className="p-4">
        <p className="text-xs text-gray-500 font-medium mb-1">{label}</p>
        <p className="text-2xl font-bold" style={{ color: accent }}>
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

function SendInviteDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: me } = useGetMe();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [contact, setContact] = useState("");
  const [targetRole, setTargetRole] = useState<UserRole>("agent");
  const createInvitation = useCreateInvitation();

  const roleOptions = invitableFor(me?.role);

  const reset = () => {
    setFirstName("");
    setLastName("");
    setContact("");
    setTargetRole("agent");
  };

  const submit = async () => {
    if (!firstName.trim() || !lastName.trim() || !contact.trim()) return;
    try {
      const result = await createInvitation.mutateAsync({
        data: {
          recipientFirstName: firstName.trim(),
          recipientLastName: lastName.trim(),
          recipientContact: contact.trim(),
          channel: "email",
          targetRole,
        },
      });
      queryClient.invalidateQueries({ queryKey: getListInvitationsQueryKey() });
      if (result.emailDelivered) {
        toast({
          title: "Invitation sent",
          description: `${firstName.trim()} ${lastName.trim()} has been invited as ${ROLE_LABELS[targetRole]}.`,
        });
      } else {
        // The invitation was saved, but the email provider rejected delivery —
        // make this loud so the inviter never assumes the recipient got a link.
        toast({
          variant: "destructive",
          title: "Saved, but the email wasn't delivered",
          description: `${firstName.trim()} won't receive an invite link yet.${
            result.emailError ? ` Reason: ${result.emailError}` : ""
          }`,
        });
      }
      reset();
      onOpenChange(false);
    } catch {
      toast({
        title: "Could not send invitation",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[460px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[#0B132B]">Send invitation</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="e.g. Thabo"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Surname</Label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="e.g. Mokoena"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select
              value={targetRole}
              onValueChange={(v) => setTargetRole(v as UserRole)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {roleOptions.map((r) => (
                  <SelectItem key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              The invitee receives this role automatically when they sign up
              with this email.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>Email address</Label>
            <Input
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              type="email"
              placeholder="name@example.co.za"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={
              !firstName.trim() ||
              !lastName.trim() ||
              !contact.trim() ||
              createInvitation.isPending
            }
            className="bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl w-full"
          >
            {createInvitation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              "Send invitation"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
